import * as THREE from 'three';

// A dark, metallic blob whose vertices are displaced by layered noise.
// It spikes outward when audio energy / breath is high.
// Shader approach: GLSL simplex noise on the vertex shader.

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uEnergy;     // 0..1, current audio/breath energy
  uniform float uSpike;      // 0..1, momentary spike
  uniform float uScale;
  varying vec3 vNormal;
  varying vec3 vPos;

  // simplex noise — Ashima
  vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
  vec4 mod289(vec4 x){return x-floor(x*(1./289.))*289.;}
  vec4 permute(vec4 x){return mod289(((x*34.)+1.)*x);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
  float snoise(vec3 v){
    const vec2 C=vec2(1./6.,1./3.);
    const vec4 D=vec4(0.,.5,1.,2.);
    vec3 i=floor(v+dot(v,C.yyy));
    vec3 x0=v-i+dot(i,C.xxx);
    vec3 g=step(x0.yzx,x0.xyz);
    vec3 l=1.-g;
    vec3 i1=min(g.xyz,l.zxy);
    vec3 i2=max(g.xyz,l.zxy);
    vec3 x1=x0-i1+C.xxx;
    vec3 x2=x0-i2+C.yyy;
    vec3 x3=x0-D.yyy;
    i=mod289(i);
    vec4 p=permute(permute(permute(
      i.z+vec4(0.,i1.z,i2.z,1.))
      +i.y+vec4(0.,i1.y,i2.y,1.))
      +i.x+vec4(0.,i1.x,i2.x,1.));
    float n_=.142857142857;
    vec3 ns=n_*D.wyz-D.xzx;
    vec4 j=p-49.*floor(p*ns.z*ns.z);
    vec4 x_=floor(j*ns.z);
    vec4 y_=floor(j-7.*x_);
    vec4 x=x_*ns.x+ns.yyyy;
    vec4 y=y_*ns.x+ns.yyyy;
    vec4 h=1.-abs(x)-abs(y);
    vec4 b0=vec4(x.xy,y.xy);
    vec4 b1=vec4(x.zw,y.zw);
    vec4 s0=floor(b0)*2.+1.;
    vec4 s1=floor(b1)*2.+1.;
    vec4 sh=-step(h,vec4(0.));
    vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
    vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
    vec3 p0=vec3(a0.xy,h.x);
    vec3 p1=vec3(a0.zw,h.y);
    vec3 p2=vec3(a1.xy,h.z);
    vec3 p3=vec3(a1.zw,h.w);
    vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
    p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
    vec4 m=max(.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);
    m=m*m;
    return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
  }

  void main(){
    vec3 n = normal;
    float t = uTime * 0.35;
    float n1 = snoise(normal * 1.4 + vec3(t, t*0.7, -t*0.9));
    float n2 = snoise(normal * 3.2 + vec3(-t*0.9, t*1.2, t*0.4)) * 0.5;
    float n3 = snoise(normal * 7.0 + vec3(t*1.6, -t*0.6, t*0.8)) * 0.25;
    float d = (n1 + n2 + n3);
    float energy = 0.15 + uEnergy * 0.55 + uSpike * 0.9;
    // concentrate the spike on positive lobes so you get "horns", not a balloon
    float spike = max(0.0, d);
    float disp = mix(d * 0.08, spike, 0.6) * energy;
    vec3 displaced = position + n * disp * uScale;
    vec4 viewPos = modelViewMatrix * vec4(displaced, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vPos = viewPos.xyz;
    gl_Position = projectionMatrix * viewPos;
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uEnergy;
  varying vec3 vNormal;
  varying vec3 vPos;
  void main(){
    vec3 V = normalize(-vPos);
    float fresnel = pow(1.0 - max(0.0, dot(normalize(vNormal), V)), 3.0);
    vec3 base = uColor;
    vec3 rim = vec3(1.0) * fresnel * (0.25 + uEnergy * 0.6);
    // slight bluish under-tone
    vec3 col = base * 0.18 + rim;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export function createFerrofluid(radius = 0.42) {
  const geometry = new THREE.IcosahedronGeometry(radius, 64);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uEnergy: { value: 0.3 },
      uSpike: { value: 0 },
      uScale: { value: 1.0 },
      uColor: { value: new THREE.Color(0x0a0a0e) },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  return mesh;
}
